import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { getSource, useSources } from "../../sources";
import { useActiveServer } from "../../lib/activeServer";
import { homePhase } from "../../lib/homePhase";
import { isResumable } from "../../lib/playState";
import { useServerHubs } from "../../lib/serverHubs";
import { useAmbientBackdrop } from "../../lib/pageBackdrop";
import { HeroBanner, MetaDot } from "../../components/hero-banner";
import { HubRow, useWatchSnapshot } from "../../components/media";
import { FilmstripIcon, PlayIcon, ServerIcon } from "../../components/icons";
import { episodeCardLines, formatRuntime } from "../../components/library/media-meta";
import { EmptyState, LoadingState } from "../../components/page-state";
import { ServerUnavailableState } from "../../components/server-unavailable";
import { lastSeenIfOffline } from "../../lib/servers";

export const Route = createFileRoute("/_authed/")({
  component: Home,
});

/**
 * Home is the ACTIVE server's front door (the bar's chip picks it):
 * hero + Continue Watching + the server's recently-added rows. Discover
 * and Watchlist carry the app-level surfaces; other servers are one chip
 * switch away and their rows stay warm in the query cache.
 */
function Home() {
  // An art route: the shell's backdrop carries the last art seen while
  // nothing of ours has decoded (lib/pageBackdrop) — first hook, before any
  // early return, so the spinner phase has it too.
  useAmbientBackdrop();
  const { server, servers, isLoading: serversLoading } = useActiveServer();
  const { isLoading: sourcesLoading } = useSources();
  useWatchSnapshot(); // a watch change re-sorts Continue Watching live
  const source = server?.online ? (getSource(server.id) ?? null) : null;
  const home = useServerHubs(source);
  // The hero is Continue Watching's first row. An EPISODE hero headlines its
  // show and wears the show's backdrop, fetched as the item page does (same
  // ["item", …] key, so the show page opens warm) — a MediaItem the ambient
  // art can rebuild a URL from, which a by-id URL could never be. Hooks stay
  // above the early returns; hubs are [] until loaded, so the query idles.
  const continueHub = home.hubs.find((h) => h.id === "continue-watching");
  const hero = continueHub?.items[0] ?? home.hubs[0]?.items[0];
  const heroShowId = hero?.type === "episode" ? (hero.episode?.grandparentId ?? null) : null;
  const heroShow = useQuery({
    queryKey: ["item", source?.id ?? "none", heroShowId ?? "none"],
    queryFn: () => source!.getItem(heroShowId as string),
    enabled: Boolean(source && heroShowId),
  });

  const phase = homePhase({
    serverCount: servers.length,
    resourcesLoading: serversLoading || sourcesLoading,
    activeOnline: server ? server.online : null,
    hasSource: source !== null,
    hubsPending: home.isLoading,
    hubsError: home.isError,
    hubCount: home.hubs.length,
  });

  // ① Plex's first two phases are one look: the amber spinner at the centre
  // of the canvas while the servers are looked up ("searching"), and again
  // while the picked server's hubs load ("loading" — also the registry lag
  // between the resources landing and the source being usable). Never a
  // welcome screen with a live "Connect a server" button for a search that
  // has not finished, and never shimmer rows that a hero then replaces.
  if (phase === "searching" || phase === "loading") {
    return <LoadingState />;
  }

  // ② Nothing claimed — the resources settled empty.
  if (phase === "no-servers") {
    return (
      <EmptyState
        icon={<ServerIcon size={28} />}
        title="Welcome to Rago."
        action={
          <Link to="/claim" className="rago-btn rago-btn-fit">
            Connect a server
          </Link>
        }
      >
        Connect your Rago Server and your libraries will fill this page.
      </EmptyState>
    );
  }

  // ③ The picked server is down. Sticky by design (resolveActiveServer) —
  // don't teleport to another server; say so and offer a retry.
  if (phase === "unavailable") {
    return (
      <ServerUnavailableState
        serverId={server!.id}
        serverName={server!.name}
        owned={server!.owned}
        lastSeenAt={lastSeenIfOffline(server!)}
      />
    );
  }

  // ④ Online per the broker but not answering from this device (no
  // lastSeenAt — a fresh heartbeat next to "unavailable" would read wrong).
  if (phase === "error") {
    return (
      <ServerUnavailableState serverId={server!.id} serverName={server!.name} owned={server!.owned} />
    );
  }

  // ⑤ Reachable, nothing in it.
  if (phase === "empty") {
    return (
      <EmptyState
        icon={<FilmstripIcon size={28} />}
        title={`${server!.name} is empty.`}
        action={
          server!.owned ? (
            <Link
              to="/settings/server/$serverId/libraries"
              params={{ serverId: server!.id }}
              className="rago-btn rago-btn-fit"
            >
              Add a library
            </Link>
          ) : (
            <p className="text-xs text-[var(--rago-text-3)]">
              Ask the server's owner to add a library.
            </p>
          )
        }
      >
        Add a library and point it at a folder of movies or TV shows — they'll
        fill this page.
      </EmptyState>
    );
  }

  // phase === "ready" implies a source (homePhase's contract); restated so
  // TypeScript narrows `source` for the render below.
  if (!source) return <LoadingState />;

  const heroState = hero ? source.getUserState(hero.id) : null;
  const heroShowItem = heroShow.data ?? null;
  const showBackdrop = heroShowItem ? source.imageUrl(heroShowItem, "backdrop", 1920) : "";
  const heroBackdrop = showBackdrop || (hero ? source.imageUrl(hero, "backdrop", 1920) : "");
  // No show backdrop: an episode's still, overscanned+blurred, beats none.
  const heroArt = heroBackdrop || (hero ? source.imageUrl(hero, "thumb", 576) : "");
  // Hold the art while the show item is in flight so a still→backdrop swap
  // never replays the hero fade.
  const heroArtPending = Boolean(heroShowId) && heroShow.isPending;
  const heroLines = hero?.type === "episode" ? episodeCardLines(hero) : null;
  const resuming = !!hero && !!heroState && isResumable(heroState, hero.runtimeMs);
  const minutesLeft =
    resuming && hero && hero.runtimeMs > 0
      ? Math.max(1, Math.round((hero.runtimeMs - heroState.viewOffsetMs) / 60_000))
      : null;

  const rowCaption = (hub: (typeof home.hubs)[number]): string =>
    `From ${server!.name}${
      hub.libraryType ? ` · ${hub.libraryType === "show" ? "TV Shows" : "Movies"}` : ""
    }`;

  return (
    <div className="pb-16">
      {hero ? (
        <HeroBanner
          eyebrow={continueHub?.items[0] === hero ? "Continue watching" : "Featured"}
          title={heroLines?.primary ?? hero.title}
          backdropUrl={heroArtPending ? undefined : heroArt || undefined}
          lowRes={!heroBackdrop && !!heroArt}
          remember={{ sourceId: source.id, item: showBackdrop && heroShowItem ? heroShowItem : hero }}
          meta={
            <>
              <span>
                {heroLines
                  ? [heroLines.tertiary, heroLines.secondary].filter(Boolean).join(" · ")
                  : hero.year}
              </span>
              {hero.runtimeMs >= 60_000 ? (
                <>
                  <MetaDot />
                  <span>{formatRuntime(hero.runtimeMs)}</span>
                </>
              ) : null}
              {hero.type !== "episode" && hero.genres.length > 0 ? (
                <>
                  <MetaDot />
                  <span>{hero.genres.slice(0, 2).join(", ")}</span>
                </>
              ) : null}
              {minutesLeft !== null ? (
                <>
                  <MetaDot />
                  <span className="text-[var(--rago-text-1)]">{minutesLeft} min left</span>
                </>
              ) : null}
            </>
          }
          actions={
            <>
              <Link
                to="/player/$sourceId/$itemId"
                params={{ sourceId: hero.sourceId, itemId: hero.id }}
                className="rago-btn"
              >
                <PlayIcon size={16} bold />
                {resuming ? "Resume" : "Play"}
              </Link>
              <Link
                to="/item/$sourceId/$itemId"
                params={{ sourceId: hero.sourceId, itemId: hero.id }}
                className="rago-btn rago-btn-quiet backdrop-blur-md"
              >
                Details
              </Link>
            </>
          }
        />
      ) : null}

      {/* relative: paints above the hero's fixed backdrop layer. The rows
          rise in last (rago-rise-4, after the hero's actions) so the rails
          land under the settling art instead of popping beside it; the
          animation ends at transform:none, so no containing block lingers. */}
      <div className={`${hero ? "relative mt-10" : "rago-page"} rago-rise rago-rise-4`}>
        {home.hubs.map((hub) => (
          <HubRow
            key={hub.id}
            hub={hub}
            sourceFor={() => source}
            caption={rowCaption(hub)}
            seeAll={
              hub.libraryId ? { sourceId: source.id, libraryId: hub.libraryId } : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}
